import React, {useState} from "react"
import {Button, Col, Modal, Row, Space, Table, Tabs, Tooltip, Typography} from "antd";
import {
    Area,
    AreaChart, Bar,
    BarChart,
    CartesianGrid, ComposedChart,
    Legend,
    Line,
    LineChart,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis
} from "recharts";
//import {mainTrends} from "./Models/RMP.flow";
import {ChartBox} from "./ChartBox";
import {compareStrategies} from "./recurrence";
import {downloadCsv} from "./exportCsv";
import {StatCard} from "../components/ui/StatCard";
import {ChartFrame, ChartHint, legendProps} from "../components/ui/ChartFrame";
import {DownloadOutlined, RightOutlined} from "@ant-design/icons";
import {axisProps, chartPalette, gridProps, numberFormat, tokens, valueAxisProps, yearAxisProps} from "../components/ui/tokens";
import * as lodash from "lodash"
import * as PropTypes from "prop-types";

const modelAB = (item) => [
    {value: item.value + 'A',  title: item.title + ' A',                                            source: item.source, kmv: (i, from) => item.kmv.A, prefix: 'A',                         selector: (i, from) => item.selector + 'A'},
    {value: item.value + 'B',  title: item.title + ' B',                                            source: item.source, kmv: (i, from) => i > from ? item.kmv.B : item.kmv.A, prefix: 'B', selector: (i, from) => i > from ? item.selector + 'B': item.selector + 'A'},
    {value: item.value + 'AN', inv: true, title: (item.titleInv || item.title + ' (альт.)') + ' A', source: item.source, kmv: (i, from) => item.kmv.B, prefix: 'A',                         selector: (i, from) => item.selector + 'A'},
    {value: item.value + 'BN', inv: true, title: (item.titleInv || item.title + ' (альт.)') +  'B', source: item.source, kmv: (i, from) => i > from ? item.kmv.B : item.kmv.B, prefix: 'B', selector: (i, from) => i > from ? item.selector + 'B': item.selector + 'A'},
]

const makeModeling = (modeling) => {
    const res = []
    modeling.forEach((item, i) => res.push(...modelAB(item, i)))
    return res

}

const TrendRender = ({data, colors, name, title, opened}) => {
    const [open, setOpen] = useState(false)
    const setClose = () => setOpen(false)
    const window2019to2030 = data.filter(item => (item.year >= 2019) && (item.year <= 2030))

    const charts = (
        <Row gutter={[16, 16]} className={"trend-row"}>
            <Col xs={24} md={12} style={{minWidth: 0}}>
                <ChartFrame title={title} hint={"Значение по стратегиям и целевой ориентир"} aspect={1.5}>
                    <LineChart data={window2019to2030} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                        <CartesianGrid {...gridProps}/>
                        <XAxis dataKey={"year"} {...yearAxisProps}/>
                        <YAxis {...valueAxisProps} domain={['dataMin - 2', 'dataMax + 2']}/>
                        <ChartTooltip content={<ChartHint unit={"%"}/>} cursor={{stroke: tokens.border}}/>
                        <Legend {...legendProps}/>
                        <Line isAnimationActive={false} type={"monotone"} dataKey={`${name}A`} name={"Стратегия А"}
                              stroke={colors[0]} strokeWidth={2} dot={false} activeDot={{r: 4}}/>
                        <Line isAnimationActive={false} type={"monotone"} dataKey={`${name}B`} name={"Стратегия В"}
                              stroke={colors[1]} strokeWidth={2} dot={false} activeDot={{r: 4}}/>
                        <Line isAnimationActive={false} type={"monotone"} dataKey={`${name}V`} name={"Целевое значение"}
                              stroke={colors[2]} strokeWidth={2} strokeDasharray={"5 4"} dot={false}/>
                    </LineChart>
                </ChartFrame>
            </Col>
            <Col xs={24} md={12} style={{minWidth: 0}}>
                <ChartFrame title={"Разница между стратегиями"} hint={"Насколько сценарий В отличается от А"}
                            aspect={1.5}>
                    <AreaChart data={window2019to2030} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                        <defs>
                            <linearGradient id={`grad-${name}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={colors[1]} stopOpacity={0.35}/>
                                <stop offset="100%" stopColor={colors[1]} stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid {...gridProps}/>
                        <XAxis dataKey={"year"} {...yearAxisProps}/>
                        <YAxis {...valueAxisProps}/>
                        <ChartTooltip content={<ChartHint unit={"п.п."}/>} cursor={{stroke: tokens.border}}/>
                        <Area isAnimationActive={false} type={"monotone"} dataKey={`${name}D`} name={"Разница"}
                              stroke={colors[1]} strokeWidth={2} fill={`url(#grad-${name})`}/>
                    </AreaChart>
                </ChartFrame>
            </Col>
        </Row>
    )

    if (opened) return charts

    return <>
        <button type={"button"} className={"trend-tile"} onClick={() => setOpen(true)}>
            <span className={"trend-tile__title"}>{title}</span>
            <span className={"trend-tile__hint"}>Показать динамику<RightOutlined/></span>
        </button>
        <Modal visible={open} onOk={setClose} onCancel={setClose} width={"1000px"} footer={null}
               title={title} style={{maxWidth: '100vw', top: 12}} bodyStyle={{padding: 16}}>
            {charts}
        </Modal>
    </>
}

const Model = ({strategy, mutation, params, flow, options, cp, region, mx, name}) => {
    const [showBeforeFirst, setShowBeforeFirst] = useState(false)
    const [pok, setPok] = useState()
    const inverseShowShowBeforeFirst = () => setShowBeforeFirst(!showBeforeFirst)

    console.log('opt', options, region)

    const fromYear = (params.fromYear || 2020) - 9
    const toYear  = ( params.toYear || fromYear) + 30

    const steps = [...flow.steps.filter(item => !(item.noCalc)), ...makeModeling(flow.modeling)]

    const connections = flow.connections
    const economics = flow.economics
    const resultDataSet = []

    const yearProto = {}

    for (const stepsKey in steps) {
        yearProto[steps[stepsKey].value] = 0
    }

    const lastFactYear = 2020
    const yearColumns = [{
        title: 'Показатель',
        dataIndex: 'key',
        key: 'd',
        fixed: 'left',
        width: 240,
        render: (text) => <span className={"data-table__name"}>{text}</span>,
    }]

    const formater = new Intl.NumberFormat('ru-RU')

    const makeV = (pok, year) => {
        if (pok == 'live5y') {
            if (year == 2019) return 55.3;
            if (year == 2020) return 55.6;
            if (year == 2021) return 57.4;
            if (year == 2022) return cp?.y5f2022 || 56.7;
            if (year == 2023) return cp?.y5f2023 || 57.2;
            if (year == 2024) return cp?.y5f2024 || 60.0;
        }
        if (pok == 'live1y') {
            if (year == 2019) return 21.7;
            if (year == 2020) return 20.6;
            if (year == 2021) return 20.3;
            if (year == 2022) return cp?.y1f2022 || 19.0;
            if (year == 2023) return cp?.y1f2023 || 18.1;
            if (year == 2024) return cp?.y1f2024 || 17.3;
        }
        return undefined;
    }

    for (let i = fromYear; i < toYear; i++) {

        if (showBeforeFirst || (i >= (params.fromYear || 2020))) {
            yearColumns.push({
                title: <span className={i > lastFactYear ? 'data-table__year data-table__year--forecast' : 'data-table__year'}>
                    {i}
                </span>,
                width: 78,
                dataIndex: i,
                align: "right",
                key: i,
                className: i > lastFactYear ? 'data-table__cell data-table__cell--forecast' : 'data-table__cell',
                render: (item) => item === undefined || item === null || isNaN(item)
                    ? <span className={"data-table__empty"}>—</span>
                    : formater.format(Math.round(item)),
            })
        }
        const currYear = {...yearProto}

        // запуситили инициализацию
        for (const stepsKey in steps) {
            const currStep = steps[stepsKey]
            if ((currStep.data)) {
                const dataHolder = (currStep.dataRegion && currStep.dataRegion[region]) || currStep

                if ((i <= 2020) && (i >= 2011))
                    currYear[currStep.value] = dataHolder?.data[i - 2011]
                else
                    currYear[currStep.value] = Math.floor(dataHolder.data[2019-2011] + (i - 2020) * dataHolder.dataPredictor *  (Math.pow(0.98, (i - 2020))))

                /*
                if ((i <= 2020) && (i >= 2011))
                    currYear[currStep.value] = currStep?.data[i - 2011]
                else
                    currYear[currStep.value] = Math.floor(currStep.data[2019-2011] + (i - 2020) * currStep.dataPredictor *  (Math.pow(0.98, (i - 2020))))
                    */

            }
        }

        // выполнили простые трансферы
        for (const connection of connections) {
            if (connection.sender)  {
                currYear[connection.target] = Math.floor(currYear[connection.target] + currYear[connection.source] * Number(connection.sender.value))
            }
        }


        // выполнили накопление контингентов
        for (const stepsKey in steps) {
            const currStep = steps[stepsKey]
            if ((i > fromYear) && (currStep.getCont)) {
                currYear[currStep.value] = Math.floor(currYear[currStep.value] + resultDataSet[resultDataSet.length - 1][currStep.value] * currStep.getCont.c1)
            }
        }

        // выполняем проход КМ
        for (const stepsKey in steps) {
            const currStep = steps[stepsKey]
            if (currStep.kmv)
            {
                currYear[currStep.value] = currYear[currStep.source]
                for (let j = 0; j < currStep.kmv(i, 2020).length; j++) {
                    const idx = resultDataSet.length - j - 1
                    if (idx >= 0)
                        currYear[currStep.value] = currYear[currStep.value] + resultDataSet[idx][currStep.source] * currStep.kmv(i, 2020)[j]
                }
                currYear[currStep.value] = Math.floor(1/100 * currYear[currStep.value] * (currStep.selectorInv ? 1 - strategy[currStep.selector(i, 2020)] : strategy[currStep.selector(i, 2020)]))
            }
        }


        resultDataSet.push({year: i, ...currYear})
    }




    const resultPlanarSet = [];

    for (const yearProtoKey in yearProto) {
        const temp = {'key': steps.find((item => item.value == yearProtoKey)).title}
        for (let i = fromYear; i < toYear; i++) {
            temp[i] = resultDataSet.find((item) => item.year == i)[yearProtoKey]
        }
        resultPlanarSet.push(temp)
    }

    console.log("rds", resultDataSet)
    const colors = chartPalette

    const resultData = resultDataSet.map(item => {
        item.h1xA = item.h1xA || 0
        item.h1xB = item.h1xB || 0
        item.h2xA = item.h2xA || 0
        item.h2xB = item.h2xB || 0
        item.h3xA = item.h3xA || 0
        item.h3xB = item.h3xB || 0
        item.h4xA = item.h4xA || 0
        item.h4xB = item.h4xB || 0
        item.h5xA = item.h5xA || 0
        item.h5xB = item.h5xB || 0

        const ttlA = item.h1xA + item.h2xA + item.h3xA + item.h4xA + item.h5xA
        const ttlB = item.h1xB + item.h2xB + item.h3xB + item.h4xB + item.h5xB

        // Затраты на таргетную терапию: число пациентов на каждой схеме,
        // умноженное на её цену (flow.economics.schemes, «Расчеты к отчету.xlsx»).
        const costOf = (suffix) => economics?.byStep
            ? Object.keys(economics.byStep).reduce(
                (sum, step) => sum + (item[step + suffix] || 0)
                    * (economics.schemes[economics.byStep[step]]?.price || 0), 0)
            : 0

        return ({
            ...item,
            ttlA,
            ttlB,
            costA: Math.round(costOf('A') / 1e6),
            costB: Math.round(costOf('B') / 1e6),
            costD: Math.round((costOf('B') - costOf('A')) / 1e6),
            savedLifes: -((item.h1xA + item.h2xA + item.h3xA + item.h4xA + item.h5xA) - (item.h1xB + item.h2xB + item.h3xB + item.h4xB +  item.h5xB)) * 0.4
        })
    })

    const trends = {
        year: lodash.range(fromYear, toYear),
        // stageI_II: flow.mainTrends.data
    }

    const mainTrends = flow.mainTrends

    const makeTrend = (trendName, addVal) => {
        const currTrend = mainTrends[trendName]
        // ряда может не быть: у новых моделей часть показателей ГРР ещё не заведена
        if (!currTrend || !currTrend.data) return trends.year.map(() => 0)
        const currData =  currTrend.data
        return trends.year.map(
            (y, i) => currData[i]  ? currData[i] : currData[currData.length - 1] + (i - currData.length) * currTrend.ltr * (Math.pow(0.98, (i - currData.length))) + addVal(y, i)
        )
    }

    if (true) {
        trends.stageI_IIA = makeTrend('stageI_II', (y, i) => 0)
        trends.stageI_IIB = makeTrend('stageI_II', (y, i) => 0)

        trends.live5yA = makeTrend('live5y', (y, i) => 0)
        trends.live5yB = makeTrend('live5y', (y, i) => 3*(resultData[i - 6].savedLifes + resultData[i - 7].savedLifes + resultData[i - 8].savedLifes + resultData[i - 9].savedLifes + resultData[i - 10].savedLifes) / resultData[i]['1']) // 3+

        trends.live1yA = makeTrend('live1y', (y, i) => 0)
        trends.live1yB = makeTrend('live1y', (y, i) => -resultData[i - 1].savedLifes * 6 / resultData[i - 1]['1']) // 0.6 => 6.0

        trends.dnlA = makeTrend('dnl', (y, i) => 0)
        trends.dnlB = makeTrend('dnl', (y, i) => resultData[i].savedLifes / resultData[i]['1'])

        trends.live5yttlA = makeTrend('live5yttl', (y, i) => 0)
        trends.live5yttlB = makeTrend('live5yttl', (y, i) => 0.14*3*(resultData[i - 6].savedLifes + resultData[i - 7].savedLifes + resultData[i - 8].savedLifes + resultData[i - 9].savedLifes + resultData[i - 10].savedLifes) / resultData[i]['1']) // 3+

        trends.live1yttlA = makeTrend('live1yttl', (y, i) => 0)
        trends.live1yttlB = makeTrend('live1yttl', (y, i) => 0.21 * -resultData[i - 1].savedLifes * 6 / resultData[i - 1]['1']) // 0.6 => 6.0
    }

    const readyTrend = trends.year.map((y,i) => {
        const data = {year: y}
        for (const trendsKey in trends) {
            data[trendsKey] = trends[trendsKey][i]
        }
        return data
    }).map(item => ({
        ...item,
        stageI_IID: (item.stageI_IIB - item.stageI_IIA) ,
        live5yD:( item.live5yB - item.live5yA ) ,
        live1yD: (item.live1yB - item.live1yA ) ,
        dnlD:    (item.dnlB - item.dnlA) ,
        live5yttlD:( item.live5yttlB - item.live5yttlA ) ,
        live1yttlD: (item.live1yttlB - item.live1yttlA ) ,
        live5yttlV: makeV('live5y', item.year),
        live1yttlV: makeV('live1y', item.year),
    }))

    // Сопоставление сценариев по рецидивам и деньгам (если модель их описывает)
    const economicsSeries = economics?.recurrences ? compareStrategies(resultData, economics, strategy) : []
    const horizonEconomics = economicsSeries.find(item => item.year === 2025) || {}
    const totalAvoided = economicsSeries
        .filter(item => item.year >= 2021 && item.year <= 2030)
        .reduce((sum, item) => sum + item.avoided, 0)
    const cumulativeTo2030 = economicsSeries.find(item => item.year === 2030)?.cumulativeNet

    // options.showTrends === false — у модели ещё нет рядов ГРР,
    // вкладки с трендами дожития скрываем, чтобы не показывать нули
    const showTrends = options?.showTrends !== false

    // Сводка: заболеваемость — за последний фактический год, эффект стратегий —
    // на горизонте прогноза (до 2020 года стратегии совпадают по построению).
    const factYear = 2020
    const horizonYear = 2025
    const fact = resultData.find(item => item.year === factYear) || {}
    const horizon = resultData.find(item => item.year === horizonYear) || {}

    const summary = <Row gutter={[12, 12]} style={{marginBottom: 16}}>
        <Col xs={12} lg={6}>
            <StatCard label={`Выявлено новых случаев, ${factYear}`} value={fact['1']} unit={"чел."}
                      hint={"Первый узел модели: заболеваемость за последний фактический год"}/>
        </Col>
        <Col xs={12} lg={6}>
            <StatCard label={`На таргетной терапии, А — ${horizonYear}`} value={horizon.ttlA} unit={"чел."}
                      tone={"primary"} hint={"Накопленный контингент при текущей практике"}/>
        </Col>
        <Col xs={12} lg={6}>
            <StatCard label={`На таргетной терапии, В — ${horizonYear}`} value={horizon.ttlB} unit={"чел."}
                      tone={"accent"} hint={"Накопленный контингент в целевом сценарии"}/>
        </Col>
        <Col xs={12} lg={6}>
            {economicsSeries.length
                ? <StatCard label={"Рецидивов предотвращено, 2021–2030"} value={totalAvoided} unit={"чел."}
                            tone={"accent"}
                            hint={"Разница числа рецидивов между сценариями А и В за десять лет"}/>
                : economics
                    ? <StatCard label={`Разница затрат В − А, ${horizonYear}`} value={horizon.costD} unit={"млн ₽"}
                                tone={horizon.costD > 0 ? 'danger' : 'accent'}
                                hint={"Затраты на таргетную терапию по ценам схем терапии"}/>
                    : <StatCard label={`Разница контингента В − А, ${horizonYear}`}
                                value={(horizon.ttlB || 0) - (horizon.ttlA || 0)} unit={"чел."} tone={"accent"}/>}
        </Col>
    </Row>

    return <>
        <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
                     flexWrap: 'wrap', marginBottom: 12}}>
            <Typography.Title level={4} style={{margin: 0, fontWeight: 600}}>Моделирование</Typography.Title>
            <Typography.Text type={"secondary"} style={{fontSize: 12}}>
                Стратегия А — текущая практика, В — целевой сценарий
            </Typography.Text>
        </div>
        {summary}
        {/* key={region}: recharts 1.8 не пересчитывает домен оси при смене данных —
              при выборе другой территории график остаётся со старой шкалой,
              поэтому перемонтируем блок целиком */}
        <Tabs key={region} destroyInactiveTabPane defaultActiveKey={showTrends ? "1" : "2"}>
            {showTrends && <Tabs.TabPane tab={"Дожитие, все ЗНО"} key={"1"}>
                <TrendRender data={readyTrend} colors={colors} name={"live5yttl"}
                             title={"На учёте 5 лет и более"} opened/>
                <TrendRender data={readyTrend} colors={colors} name={"live1yttl"}
                             title={"Одногодичная летальность"} opened/>
            </Tabs.TabPane>}
            {showTrends && <Tabs.TabPane tab={name || "Нозология"} key={"4"}>
                <TrendRender data={readyTrend} colors={colors} name={"live5y"}
                             title={"На учёте 5 лет и более"} opened/>
                <TrendRender data={readyTrend} colors={colors} name={"live1y"}
                             title={"Одногодичная летальность"} opened/>
            </Tabs.TabPane>}
            {economicsSeries.length > 0 && <Tabs.TabPane tab={"Экономика"} key={"5"}>
                <Typography.Paragraph type={"secondary"} style={{fontSize: 13}}>
                    Пертузумаб в адъюванте дороже трастузумаба, но снижает число рецидивов,
                    а лечение метастатической стадии обходится в {numberFormat.format(Math.round(economics.recurrenceCostByAge[0] / 1e6))} млн ₽
                    за первый год и продолжается ещё десять лет. Ниже — во что обходится
                    доплата и сколько удаётся сберечь; расчёт повторяет лист «Аудит-Перьета».
                </Typography.Paragraph>

                <Row gutter={[12, 12]} style={{marginBottom: 16}}>
                    <Col xs={12} lg={6}>
                        <StatCard label={"Рецидивов при сценарии А, 2025"} value={horizonEconomics.recurrencesA}
                                  unit={"чел."} hint={"Текущая практика"}/>
                    </Col>
                    <Col xs={12} lg={6}>
                        <StatCard label={"Рецидивов при сценарии В, 2025"} value={horizonEconomics.recurrencesB}
                                  unit={"чел."} tone={"accent"} hint={"Целевая доля пертузумаба"}/>
                    </Col>
                    <Col xs={12} lg={6}>
                        <StatCard label={"Экономия на лечении рецидивов, 2025"} value={horizonEconomics.saved}
                                  unit={"млн ₽"} tone={"accent"}
                                  hint={"Меньше рецидивов — меньше затрат на метастатическую стадию"}/>
                    </Col>
                    <Col xs={12} lg={6}>
                        <StatCard label={"Итог с учётом доплаты, 2025"} value={horizonEconomics.net}
                                  unit={"млн ₽"} tone={horizonEconomics.net >= 0 ? 'accent' : 'danger'}
                                  hint={"Экономия на рецидивах минус доплата за адъювантную терапию за год"}/>
                    </Col>
                    <Col xs={12} lg={6}>
                        <StatCard label={"Цена предотвращённого рецидива, А → В"}
                                  value={economicsSeries.find(item => item.year === 2030)?.costPerAvoided}
                                  unit={"млн ₽"} tone={"primary"}
                                  hint={"Доплата за терапию, делённая на число предотвращённых рецидивов " +
                                        "(накопленным итогом). В исходной модели — «цена предотвращённого рецидива»"}/>
                    </Col>
                    <Col xs={12} lg={6}>
                        <StatCard label={"Накопленный итог к 2030"} value={cumulativeTo2030}
                                  unit={"млн ₽"} tone={cumulativeTo2030 >= 0 ? 'accent' : 'danger'}
                                  hint={"Сумма годовых итогов: отрицательное значение — доплата пока не окупилась"}/>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12} style={{minWidth: 0}}>
                        <ChartFrame title={"Число рецидивов"} hint={"Новые случаи метастатической болезни за год"}
                                    aspect={1.6}>
                            <LineChart data={economicsSeries} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                                <CartesianGrid {...gridProps}/>
                                <XAxis dataKey={"year"} {...yearAxisProps}/>
                                <YAxis {...valueAxisProps}/>
                                <ChartTooltip content={<ChartHint unit={"чел."}/>} cursor={{stroke: tokens.border}}/>
                                <Legend {...legendProps}/>
                                <Line isAnimationActive={false} type={"monotone"} dataKey={"recurrencesA"}
                                      name={"Сценарий А"} stroke={colors[0]} strokeWidth={2} dot={false}
                                      activeDot={{r: 4}}/>
                                <Line isAnimationActive={false} type={"monotone"} dataKey={"recurrencesB"}
                                      name={"Сценарий В"} stroke={colors[1]} strokeWidth={2} dot={false}
                                      activeDot={{r: 4}}/>
                            </LineChart>
                        </ChartFrame>
                    </Col>
                    <Col xs={24} lg={12} style={{minWidth: 0}}>
                        <ChartFrame title={"Доплата и экономия"} hint={"Столбцы — за год, линия — накопленным итогом"}
                                    aspect={1.6}>
                            <ComposedChart data={economicsSeries} margin={{top: 4, right: 8, left: 0, bottom: 0}}
                                           stackOffset={"sign"}>
                                <CartesianGrid {...gridProps}/>
                                <XAxis dataKey={"year"} {...yearAxisProps}/>
                                <YAxis {...valueAxisProps}/>
                                <ChartTooltip content={<ChartHint unit={"млн ₽"}/>} cursor={{fill: 'rgba(15,23,42,0.04)'}}/>
                                <Legend {...legendProps}/>
                                <Bar dataKey={"extra"} name={"Доплата за терапию"} fill={colors[3]}
                                     radius={[3, 3, 0, 0]} maxBarSize={14}/>
                                <Bar dataKey={"saved"} name={"Экономия на рецидивах"} fill={colors[1]}
                                     radius={[3, 3, 0, 0]} maxBarSize={14}/>
                                <Line isAnimationActive={false} type={"monotone"} dataKey={"cumulativeNet"}
                                      name={"Итог накопленным итогом"} stroke={colors[0]} strokeWidth={2} dot={false}/>
                            </ComposedChart>
                        </ChartFrame>
                    </Col>
                </Row>
            </Tabs.TabPane>}
            <Tabs.TabPane tab={"Поток пациентов"} key={"2"}>
                <Row gutter={[16, 16]}>
                    {options?.mainView &&
                        <Col span={24} style={{minWidth: 0}}>
                            <ChartFrame title={options?.mainView?.title} hint={"Пациенты по годам, человек"}
                                        aspect={3.0} minHeight={240}>
                                <LineChart data={resultData} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                                    <CartesianGrid {...gridProps}/>
                                    <XAxis dataKey={"year"} {...yearAxisProps}/>
                                    <YAxis {...valueAxisProps}/>
                                    <ChartTooltip content={<ChartHint unit={"чел."}/>} cursor={{stroke: tokens.border}}/>
                                    <Legend {...legendProps}/>
                                    {options.mainView.lines.map((line, i) =>
                                        <Line key={line.key} isAnimationActive={false} type={"monotone"}
                                              dataKey={line.key} name={line.title}
                                              stroke={colors[i % colors.length]} strokeWidth={2}
                                              dot={false} activeDot={{r: 4}}/>)}
                                </LineChart>
                            </ChartFrame>
                        </Col>}

                    <Col span={24} style={{minWidth: 0}}>
                <div className={"trend-tiles"}>
                    <Typography.Text type={"secondary"} className={"trend-tiles__label"}>
                        Показатели службы — нажмите, чтобы увидеть динамику и разницу между стратегиями
                    </Typography.Text>
                    <div className={"trend-tiles__grid"}>
                        <TrendRender data={readyTrend} colors={colors} name={"stageI_II"}
                                     title={"Выявлено на ранних стадиях"}/>
                        <TrendRender data={readyTrend} colors={colors} name={"live5y"}
                                     title={"На учёте 5 лет и более"}/>
                        <TrendRender data={readyTrend} colors={colors} name={"live1y"}
                                     title={"Одногодичная летальность"}/>
                        <TrendRender data={readyTrend} colors={colors} name={"dnl"}
                                     title={"Прошло диагностику и лечение"}/>
                    </div>
                </div>
                    </Col>

                    <Col xs={24} md={12} style={{minWidth: 0}}>
                        <ChartFrame title={"Пациентов на таргетной терапии"}
                                    hint={"Накопленный контингент по сценариям"} aspect={1.6}>
                            <LineChart data={resultData} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                                <CartesianGrid {...gridProps}/>
                                <XAxis dataKey={"year"} {...yearAxisProps}/>
                                <YAxis {...valueAxisProps}/>
                                <ChartTooltip content={<ChartHint unit={"чел."}/>} cursor={{stroke: tokens.border}}/>
                                <Legend {...legendProps}/>
                                <Line isAnimationActive={false} type={"monotone"} dataKey={"ttlA"} name={"Сценарий А"}
                                      stroke={colors[0]} strokeWidth={2} dot={false} activeDot={{r: 4}}/>
                                <Line isAnimationActive={false} type={"monotone"} dataKey={"ttlB"} name={"Сценарий В"}
                                      stroke={colors[1]} strokeWidth={2} dot={false} activeDot={{r: 4}}/>
                            </LineChart>
                        </ChartFrame>
                    </Col>

                    <Col xs={24} md={12} style={{minWidth: 0}}>
                        <ChartFrame title={"Спасённые жизни"} hint={"Оценка выигрыша сценария В против А"}
                                    aspect={1.6}>
                            <AreaChart data={resultData} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                                <defs>
                                    <linearGradient id="savedLifesGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={colors[1]} stopOpacity={0.35}/>
                                        <stop offset="100%" stopColor={colors[1]} stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid {...gridProps}/>
                                <XAxis dataKey={"year"} {...yearAxisProps}/>
                                <YAxis {...valueAxisProps}/>
                                <ChartTooltip content={<ChartHint unit={"чел."}/>} cursor={{stroke: tokens.border}}/>
                                <Area isAnimationActive={false} type={"monotone"} dataKey={"savedLifes"}
                                      name={"Спасённые жизни"} stroke={colors[1]} strokeWidth={2}
                                      fill={"url(#savedLifesGradient)"}/>
                            </AreaChart>
                        </ChartFrame>
                    </Col>
                </Row>

            </Tabs.TabPane>
            <Tabs.TabPane tab={"Таблица данных"} key={"3"}>
                <div className={"data-table"}>
                    <div className={"data-table__head"}>
                        <div>
                            <Typography.Text className={"data-table__title"}>
                                Показатели модели по годам
                            </Typography.Text>
                            <Typography.Text type={"secondary"} className={"data-table__hint"}>
                                До {lastFactYear} года — фактические данные, дальше — прогноз
                            </Typography.Text>
                        </div>
                        <Space size={8} wrap>
                            <Button size={"small"} onClick={inverseShowShowBeforeFirst}>
                                {showBeforeFirst ? 'Скрыть ретроспективу' : 'Показать ретроспективу'}
                            </Button>
                            <Button size={"small"} icon={<DownloadOutlined/>}
                                    onClick={() => downloadCsv(resultPlanarSet, yearColumns, name)}>
                                Выгрузить CSV
                            </Button>
                        </Space>
                    </div>
                    <Table dataSource={resultPlanarSet} columns={yearColumns} size={"small"}
                           rowKey={"key"} bordered={false}
                           scroll={{x: 'max-content', y: 420}} pagination={false}/>
                </div>
            </Tabs.TabPane>
        </Tabs>
    </>
}

export default Model